/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { apiClient } from './client.js';

export type FollowUpType =
  | 'proforma_declined'
  | 'payment_overdue'
  | 'intake_pending'
  | 'session_missed'
  | 'document_pending'
  | 'review_due'
  | 'custom';
export type FollowUpPriority = 'low' | 'medium' | 'high' | 'urgent';
export type FollowUpStatus = 'open' | 'in_progress' | 'resolved';

export interface FollowUp {
  readonly id: string;
  readonly type: FollowUpType;
  readonly priority: FollowUpPriority;
  readonly status: FollowUpStatus;
  readonly title: string;
  readonly description: string | null;
  readonly clientId: string | null;
  readonly clientName: string | null;
  readonly assignedToUserId: string | null;
  readonly assignedToName: string | null;
  readonly slaDueAt: string | null;
  readonly resolvedAt: string | null;
  readonly resolutionNote: string | null;
  readonly createdAt: string;
  readonly overdue: boolean;
}

export interface FollowUpAlerts {
  readonly open: number;
  readonly overdue: number;
  readonly urgent: number;
  readonly items: readonly FollowUp[];
}

/** SLA resolution analytics (CR-011). */
export interface FollowUpAnalytics {
  readonly resolved: number;
  readonly onTime: number;
  readonly breached: number;
  readonly avgResolutionHours: number | null;
  readonly openOverdue: number;
  readonly byType: readonly {
    readonly type: FollowUpType;
    readonly total: number;
    readonly breached: number;
  }[];
}

export interface CreateFollowUpPayload {
  readonly type: FollowUpType;
  readonly priority: FollowUpPriority;
  readonly title: string;
  readonly description?: string;
  readonly clientId?: string;
  readonly slaDueAt?: string;
}

export interface UpdateFollowUpPayload {
  readonly status?: FollowUpStatus;
  readonly priority?: FollowUpPriority;
  readonly assignedToUserId?: string;
}

export interface FollowUpsQuery {
  readonly status?: FollowUpStatus;
  readonly priority?: FollowUpPriority;
}

const BASE = '/follow-ups';

export const followUpsApi = {
  async list(query: FollowUpsQuery = {}): Promise<readonly FollowUp[]> {
    const { data } = await apiClient.get<readonly FollowUp[]>(BASE, { params: query });
    return data;
  },
  async alerts(): Promise<FollowUpAlerts> {
    const { data } = await apiClient.get<FollowUpAlerts>(`${BASE}/alerts`);
    return data;
  },
  async analytics(): Promise<FollowUpAnalytics> {
    const { data } = await apiClient.get<FollowUpAnalytics>(`${BASE}/analytics`);
    return data;
  },
  async create(payload: CreateFollowUpPayload): Promise<FollowUp> {
    const { data } = await apiClient.post<FollowUp>(BASE, payload);
    return data;
  },
  async update(id: string, payload: UpdateFollowUpPayload): Promise<FollowUp> {
    const { data } = await apiClient.patch<FollowUp>(`${BASE}/${id}`, payload);
    return data;
  },
  async resolve(id: string, resolutionNote: string): Promise<FollowUp> {
    const { data } = await apiClient.post<FollowUp>(`${BASE}/${id}/resolve`, { resolutionNote });
    return data;
  },
};
